import React, { useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft } from "lucide-react";
import { useStickToBottom } from "../../../../../../hooks/useStickToBottom";
import useScrollLock from "../../../../../../hooks/useScrollLock";
import { ProductDetailsModal } from "../../../../../../interfaces/ProductObject";
import {
  selectPlacedRounds,
  totalBillAmount,
} from "../../../../../../services/helpers/DineInSummaryHelper";
import { DineInTableTile as TileData, formatElapsed } from "./dineInBoardBuilder";
import { DINE_IN_TABLE_STATE_META } from "./dineInBoardStatusDefs";
import { buildKitchenRounds, summariseKitchenRounds } from "./dineInDetailBuilder";
import { useDineInBoardActions } from "./useDineInBoardActions";
import DineInRoundGroup from "./DineInRoundGroup";
import DineInQrModal from "./DineInQrModal";
import DineInRejectReasonModal from "./DineInRejectReasonModal";
import DineInBillReviewModal from "./DineInBillReviewModal";
import "./styles/DineInBoardStates.css";
import "./styles/DineInDarkTheme.css";
import "./styles/DineInTableDetail.css";

interface DineInTableDetailProps {
  tile: TileData;
  businessId: string;
  businessName: string;
  businessLogoUrl?: string;
  products: ProductDetailsModal[];
  now: number;
  onBack: () => void;
}

const formatClock = (iso: string | null) =>
  iso ? new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "";

/**
 * One table's kitchen ticket, as a full-screen working surface.
 *
 * Portalled to document.body and fixed over everything at --z-overlay (400):
 * above the app header (200) and bottom nav (250) so nothing else is reachable
 * while a table is open, and below the modal tier (499+) so the reject, QR and
 * bill dialogs still stack ON TOP of it. A ticket is read standing up, mid
 * service — the business page's banner, tabs and app chrome are noise here,
 * and the only way out is the back arrow (or browser/hardware back, since the
 * open table lives in the URL).
 *
 * Portalling rather than replacing the board matters: DineInTab stays mounted
 * behind, so its focus-resync keeps running and its scroll position survives.
 * Backing out lands on a live, unmoved floor.
 */
const DineInTableDetail: React.FC<DineInTableDetailProps> = ({
  tile,
  businessId,
  businessName,
  businessLogoUrl,
  products,
  now,
  onBack,
}) => {
  const actions = useDineInBoardActions(businessId);
  // Ordinary local UI state, not a board action: the QR is generated entirely
  // client-side from the table's deep link, so there's no call to make.
  const [qrOpen, setQrOpen] = useState(false);
  const [billOpen, setBillOpen] = useState(false);
  const screenRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomAnchorRef = useRef<HTMLDivElement>(null);
  const meta = DINE_IN_TABLE_STATE_META[tile.state];
  const entry = tile.session;
  const memberCount = entry?.members.length ?? 0;
  const elapsed = formatElapsed(tile.openedAt, now);

  // Freeze the page underneath. The third argument names the one region that
  // IS still allowed to scroll — without it, iOS treats a touch-drag anywhere
  // as a page bounce and the ticket won't scroll at all.
  useScrollLock(true, screenRef, ".dine-in-screen__scroll");

  // Only rounds the table has actually placed belong on a kitchen ticket. The
  // endpoint DOES return drafts, so this is load-bearing rather than defensive
  // — and it's the same call the board tiles make, which is the whole point of
  // selectPlacedRounds: one table can't report different numbers on the grid
  // and on its own ticket.
  const { rounds: placedRounds, items: placedItems } = useMemo(
    () => selectPlacedRounds(entry?.rounds ?? [], entry?.roundItems ?? []),
    [entry]
  );

  const rounds = useMemo(
    () => buildKitchenRounds(placedRounds, placedItems, products),
    [placedRounds, placedItems, products]
  );
  const { roundCount, itemCount } = useMemo(
    () => summariseKitchenRounds(rounds),
    [rounds]
  );

  // Computed from placed items, exactly as the tile is. Both narrow through
  // selectPlacedRounds, so the grid figure and this one are the same number by
  // construction rather than by coincidence.
  const billTotal = useMemo(
    () => totalBillAmount(placedItems, products),
    [placedItems, products]
  );

  // Content fingerprint, NOT a render counter: the board refetches silently on
  // every focus, and re-pinning the scroll each time would drag staff off
  // whatever they'd scrolled up to read. Only a genuine change in rounds,
  // lines or statuses should count as new content.
  const contentSignature = useMemo(
    () =>
      rounds
        .map((r) => `${r.roundId}:${r.items.map((i) => `${i.key}x${i.quantity}`).join(",")}`)
        .join("|"),
    [rounds]
  );

  // scrollRef, not the document — this panel scrolls itself. See the hook.
  useStickToBottom(bottomAnchorRef, contentSignature, scrollRef);

  return createPortal(
    <div
      ref={screenRef}
      className={`dine-in-screen dine-in-dark dine-in-state--${meta.modifier}`}
      role="dialog"
      aria-modal="true"
      aria-label={`Table ${tile.tableNumber}`}
    >
      {/* ── Fixed head ─────────────────────────────────────────────────── */}
      <div className="dine-in-screen__head">
        <div className="dine-in-screen__band dine-in-detail__topbar">
          <button
            type="button"
            className="dine-in-detail__back"
            onClick={onBack}
            aria-label="Back to all tables"
          >
            <ArrowLeft size={22} strokeWidth={2.2} />
          </button>

          <div className="dine-in-detail__heading">
            <h2 className="dine-in-detail__title">TABLE {tile.label.replace(/^T/, "")}</h2>
            <p className="dine-in-detail__sub">
              {entry
                ? `${memberCount} guest${memberCount === 1 ? "" : "s"} · started ${formatClock(tile.openedAt)}`
                : "No one seated"}
            </p>
          </div>

          {entry && (
            <span className="dine-in-detail__pill">
              <span className="dine-in-detail__pill-dot" />
              {tile.pendingCount > 0 ? meta.label : "Active"}
              {elapsed && ` · ${elapsed}`}
            </span>
          )}
        </div>

        {entry && (
          <div className="dine-in-screen__band dine-in-detail__summary">
            <div className="dine-in-detail__figure">
              <span className="dine-in-detail__figure-value">
                ₹{Math.round(billTotal).toLocaleString("en-IN")}
              </span>
              <span className="dine-in-detail__figure-label">Running total</span>
            </div>
            <div className="dine-in-detail__figure">
              <span className="dine-in-detail__figure-value">{itemCount}</span>
              <span className="dine-in-detail__figure-label">
                Items · {roundCount} round{roundCount === 1 ? "" : "s"}
              </span>
            </div>
            <div className="dine-in-detail__summary-actions">
              {/* Same destination as the footer button. Two entry points to
                  one review screen, rather than two buttons that each do
                  something slightly different with the word "bill". */}
              <button
                type="button"
                className="dine-in-detail__ghost-btn"
                onClick={() => setBillOpen(true)}
              >
                Bill
              </button>
              <button
                type="button"
                className="dine-in-detail__ghost-btn"
                onClick={() => setQrOpen(true)}
              >
                Table QR
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Scrolling body ─────────────────────────────────────────────── */}
      <div className="dine-in-screen__scroll" ref={scrollRef}>
        <div className="dine-in-screen__band dine-in-detail__rounds">
          {!entry ? (
            <p className="dine-in-detail__empty">
              Nobody is seated at this table right now. It'll appear here as soon
              as someone scans its QR code.
            </p>
          ) : rounds.length === 0 ? (
            <p className="dine-in-detail__empty">
              This table hasn't placed an order yet — they're still choosing.
            </p>
          ) : (
            rounds.map((round) => (
              <DineInRoundGroup
                key={round.roundId}
                round={round}
                products={products}
                actions={actions}
              />
            ))
          )}
          {/* Scroll target for useStickToBottom — inside the scrolling region
              so "the bottom" means the newest round, not the action bar. */}
          <div ref={bottomAnchorRef} aria-hidden="true" />
        </div>
      </div>

      {/* ── Fixed foot ─────────────────────────────────────────────────── */}
      {entry && (
        <div className="dine-in-screen__foot">
          <div className="dine-in-screen__band dine-in-detail__footer">
            <div className="dine-in-detail__footer-total">
              <span className="dine-in-detail__footer-value">
                ₹{Math.round(billTotal).toLocaleString("en-IN")}
              </span>
              {/* No tax model exists anywhere in the app, so this deliberately
                  does NOT claim "incl. taxes" the way the mockup does — the
                  number is the sum of line prices and nothing more. */}
              <span className="dine-in-detail__footer-label">Total</span>
            </div>
            {/* Opens the review rather than billing directly. Closing a table
                out is the one irreversible thing this screen does, so it gets
                a look at the itemised list first. */}
            <button
              type="button"
              className="dine-in-detail__close-btn"
              onClick={() => setBillOpen(true)}
            >
              Generate Bill
            </button>
          </div>
        </div>
      )}

      {billOpen && entry && (
        <DineInBillReviewModal
          tableNumber={tile.tableNumber}
          placedItems={placedItems}
          products={products}
          onClose={() => setBillOpen(false)}
          onGenerate={() => {
            setBillOpen(false);
            actions.generateBill(entry.session.sessionId);
          }}
        />
      )}

      {actions.rejectTarget && (
        <DineInRejectReasonModal
          item={actions.rejectTarget}
          product={products.find((p) => p.productId === actions.rejectTarget?.productId)}
          busy={actions.isDeciding}
          onCancel={actions.cancelReject}
          onConfirm={actions.confirmReject}
        />
      )}

      {qrOpen && (
        <DineInQrModal
          businessId={businessId}
          businessName={businessName}
          businessLogoUrl={businessLogoUrl}
          tableNumber={tile.tableNumber}
          onClose={() => setQrOpen(false)}
        />
      )}
    </div>,
    document.body
  );
};

export default DineInTableDetail;
